import {
  AlertCircle,
  DollarSign,
  Percent,
  PiggyBank,
  RotateCcw,
  ShoppingCart,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import type { DashboardReport } from "@/types";
import { MetricCard } from "./MetricCard";

interface KpiGridProps {
  report: DashboardReport;
}

export function KpiGrid({ report }: KpiGridProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="Revenue"
        value={formatCurrency(report.totalRevenue)}
        icon={DollarSign}
        current={report.totalRevenue}
        previous={report.previous.totalRevenue}
      />
      <MetricCard
        title="Orders"
        value={String(report.completedOrders)}
        icon={ShoppingCart}
        hint={
          report.cancelledOrders > 0
            ? `${report.cancelledOrders} cancelled`
            : undefined
        }
        current={report.completedOrders}
        previous={report.previous.totalOrders}
      />
      <MetricCard
        title="Avg. order"
        value={formatCurrency(report.averageOrderValue)}
        icon={TrendingUp}
        current={report.averageOrderValue}
        previous={report.previous.averageOrderValue}
      />
      <MetricCard
        title="Gross profit"
        value={formatCurrency(report.grossProfit)}
        icon={PiggyBank}
        hint={`${formatCurrency(report.totalCost)} cost of goods`}
        current={report.grossProfit}
        previous={report.previous.grossProfit}
      />
      <MetricCard
        title="Profit margin"
        value={`${(report.profitMargin * 100).toFixed(1)}%`}
        icon={Percent}
        hint="Profit ÷ net sales (excl. tips)"
      />
      <MetricCard
        title="Visits"
        value={String(report.customers.visits)}
        icon={Users}
        hint={`${report.customers.uniqueCustomers} unique`}
        current={report.customers.visits}
        previous={report.previous.uniqueCustomers}
      />
      <MetricCard
        title="New customers"
        value={String(report.customers.newCustomers)}
        icon={UserPlus}
        hint="Created in this period"
      />
      <MetricCard
        title="Repeat rate"
        value={`${(report.customers.repeatRate * 100).toFixed(0)}%`}
        icon={Users}
        hint="Linked visits beyond first"
      />
      <MetricCard
        title="Cancellation rate"
        value={`${(report.cancellationRate * 100).toFixed(1)}%`}
        icon={AlertCircle}
        hint={`${report.cancelledOrders} of ${report.totalOrders}`}
        inverted
      />
      <MetricCard
        title="Refunds"
        value={formatCurrency(report.refunds.totalRefunded)}
        icon={RotateCcw}
        hint={`${report.refunds.refundCount} approved`}
        inverted
      />
    </div>
  );
}
